import React from 'react';
import { LocationData } from '../types';

// ─── Isometric projection ────────────────────────────────────────────────────
// gx = right-ish axis, gy = left-ish axis (depth), gz = up
const TW = 28;   // screen px per x grid unit
const TH = 14;   // screen px per y grid unit (TW/2 for classic 2:1 iso)
const TZ = 20;   // screen px per z grid unit (height)

function iso(gx: number, gy: number, gz = 0) {
  return { x: (gx - gy) * (TW / 2), y: (gx + gy) * (TH / 2) - gz * TZ };
}

function pts(...c: { x: number; y: number }[]) {
  return c.map(v => `${v.x.toFixed(1)},${v.y.toFixed(1)}`).join(' ');
}

// ─── Isometric box ───────────────────────────────────────────────────────────
interface BoxProps {
  gx: number; gy: number; gz?: number;
  w: number; d: number; h: number;
  top: string; left: string; right: string;
  stroke?: string;
}
function IsoBox({ gx, gy, gz = 0, w, d, h, top, left, right, stroke = '#0a0a0a' }: BoxProps) {
  const b10 = iso(gx + w, gy, gz);
  const b11 = iso(gx + w, gy + d, gz);
  const b01 = iso(gx, gy + d, gz);
  const t00 = iso(gx, gy, gz + h);
  const t10 = iso(gx + w, gy, gz + h);
  const t11 = iso(gx + w, gy + d, gz + h);
  const t01 = iso(gx, gy + d, gz + h);
  return (
    <g>
      <polygon points={pts(b10, b11, t11, t10)} fill={right} stroke={stroke} strokeWidth="0.5" />
      <polygon points={pts(b01, b11, t11, t01)} fill={left} stroke={stroke} strokeWidth="0.5" />
      <polygon points={pts(t00, t10, t11, t01)} fill={top} stroke={stroke} strokeWidth="0.5" />
    </g>
  );
}

// ─── Server rack (with blue LED strip on front face) ─────────────────────────
function Rack({ gx, gy, hasFront = true }: { gx: number; gy: number; hasFront?: boolean }) {
  const w = 1, d = 1, h = 3.5;
  // front face = gy+d side (left face in screen)
  const t11 = iso(gx + w, gy + d, h);
  const t01 = iso(gx, gy + d, h);
  const l11 = iso(gx + w, gy + d, h - 0.22);
  const l01 = iso(gx, gy + d, h - 0.22);
  // slot lines on front face
  const slots: React.ReactNode[] = [];
  for (let s = 0; s < 18; s++) {
    const sz = h - 0.45 - s * 0.17;
    if (sz < 0.2) break;
    const p0 = iso(gx, gy + d, sz);
    const p1 = iso(gx + w, gy + d, sz);
    slots.push(<line key={s} x1={p0.x} y1={p0.y} x2={p1.x} y2={p1.y} stroke="#252535" strokeWidth="0.5" />);
  }
  return (
    <g>
      <IsoBox gx={gx} gy={gy} w={w} d={d} h={h}
        top="#252530" left="#111118" right="#1a1a22" />
      {hasFront && <>
        <polygon points={pts(l01, l11, t11, t01)} fill="#2563eb" opacity="0.8" />
        {slots}
      </>}
    </g>
  );
}

// ─── Floor tile ──────────────────────────────────────────────────────────────
function FloorTile({ gx, gy, fill }: { gx: number; gy: number; fill: string }) {
  const a = iso(gx, gy);
  const b = iso(gx + 1, gy);
  const c = iso(gx + 1, gy + 1);
  const d = iso(gx, gy + 1);
  return <polygon points={pts(a, b, c, d)} fill={fill} stroke="#999" strokeWidth="0.3" />;
}

// ─── CRAC unit ───────────────────────────────────────────────────────────────
function Crac({ gx, gy }: { gx: number; gy: number }) {
  return (
    <g>
      <IsoBox gx={gx} gy={gy} w={1.2} d={1.6} h={2.2}
        top="#dde8ef" left="#a8bfc9" right="#c0d4db" />
      {/* vent slits on front */}
      {[0.4, 0.7, 1.0, 1.3, 1.6].map(z => {
        const p0 = iso(gx, gy + 1.6, z);
        const p1 = iso(gx + 1.2, gy + 1.6, z);
        return <line key={z} x1={p0.x} y1={p0.y} x2={p1.x} y2={p1.y} stroke="#8aaab5" strokeWidth="0.8" />;
      })}
    </g>
  );
}

// ─── Pipe ─────────────────────────────────────────────────────────────────────
function Pipe({ gx, gy, len, top, side }: { gx: number; gy: number; len: number; top: string; side: string }) {
  return (
    <IsoBox gx={gx} gy={gy} w={len} d={0.18} h={0.26}
      top={top} left={side} right={side} />
  );
}

// ─── Chiller unit ────────────────────────────────────────────────────────────
function Chiller({ gx, gy }: { gx: number; gy: number }) {
  return (
    <g>
      <IsoBox gx={gx} gy={gy} w={1.8} d={2.2} h={2.8}
        top="#e8eef0" left="#c0cfd4" right="#d4dde0" />
      {/* blue pipe connector on top */}
      <IsoBox gx={gx + 0.2} gy={gy + 0.1} gz={2.8} w={0.4} d={0.18} h={0.3}
        top="#3b82f6" left="#1d4ed8" right="#2563eb" />
      {/* red pipe connector on top */}
      <IsoBox gx={gx + 0.8} gy={gy + 0.1} gz={2.8} w={0.4} d={0.18} h={0.3}
        top="#ef4444" left="#b91c1c" right="#dc2626" />
    </g>
  );
}

// ─── UPS unit ─────────────────────────────────────────────────────────────────
function Ups({ gx, gy }: { gx: number; gy: number }) {
  return (
    <g>
      <IsoBox gx={gx} gy={gy} w={1.6} d={1.8} h={1.8}
        top="#f0f0e4" left="#c8c8b8" right="#dcdccc" />
      {/* status strip */}
      <IsoBox gx={gx} gy={gy + 1.8} gz={1.0} w={1.6} d={0.05} h={0.6}
        top="#22c55e" left="#16a34a" right="#15803d" />
    </g>
  );
}

// ─── Cooling tower ────────────────────────────────────────────────────────────
function CoolingTower({ gx, gy }: { gx: number; gy: number }) {
  return (
    <g>
      <IsoBox gx={gx} gy={gy} w={1.8} d={1.2} h={2.0}
        top="#e8e8e8" left="#bfbfbf" right="#d4d4d4" />
      {/* fan grille lines on top */}
      {[0.4, 0.9].map(x => {
        const p0 = iso(gx + x, gy, 2.0);
        const p1 = iso(gx + x, gy + 1.2, 2.0);
        return <line key={x} x1={p0.x} y1={p0.y} x2={p1.x} y2={p1.y} stroke="#a0a0a0" strokeWidth="0.8" />;
      })}
      {[0.4, 0.8].map(y => {
        const p0 = iso(gx, gy + y, 2.0);
        const p1 = iso(gx + 1.8, gy + y, 2.0);
        return <line key={y} x1={p0.x} y1={p0.y} x2={p1.x} y2={p1.y} stroke="#a0a0a0" strokeWidth="0.8" />;
      })}
    </g>
  );
}

// ─── SVG text label ───────────────────────────────────────────────────────────
function IsoLabel({ gx, gy, gz = 0, text, color = '#fff', size = 7 }: {
  gx: number; gy: number; gz?: number; text: string; color?: string; size?: number;
}) {
  const { x, y } = iso(gx, gy, gz);
  return (
    <text x={x} y={y} fill={color} fontSize={size} fontFamily="monospace"
      textAnchor="middle" dominantBaseline="middle"
      style={{ pointerEvents: 'none', userSelect: 'none' }}>
      {text}
    </text>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
interface Props {
  locationData?: LocationData | null;
}

const IsometricDatacenterView: React.FC<Props> = ({ locationData }) => {
  // Layout:
  // Server room  gy=0..9  (back / upper area)
  // Plant room   gy=9..14 (front / lower area)
  // Rack rows at gy=1, 3, 5, 7  (17 racks each, gx=1..17)
  // Cold aisles at gy=2, 6
  // Hot aisles at gy=4, 8

  const RACK_COUNT = 17;
  const RACK_START = 1;

  // ── Floor tiles ─────────────────────────────────────────────────────────────
  const floorTiles: React.ReactNode[] = [];
  for (let gx = 0; gx < 20; gx++) {
    for (let gy = 0; gy < 14; gy++) {
      let fill: string;
      if (gy >= 9) {
        // Plant room — slightly darker concrete
        fill = (gx + gy) % 2 === 0 ? '#6e7880' : '#788088';
      } else if (gy === 2 || gy === 6) {
        // Cold aisle — blue tint
        fill = '#a8c4d8';
      } else if (gy === 4 || gy === 8) {
        // Hot aisle — warm tint
        fill = '#d8b8a0';
      } else {
        // General floor
        fill = (gx + gy) % 2 === 0 ? '#a0a8b0' : '#949ca4';
      }
      floorTiles.push(<FloorTile key={`f${gx},${gy}`} gx={gx} gy={gy} fill={fill} />);
    }
  }

  // ── Back wall (gy=0 face, visible right side) ─────────────────────────────
  const backWall: React.ReactNode[] = [];
  for (let gx = 0; gx < 20; gx++) {
    const b = iso(gx, 0, 0);
    const bR = iso(gx + 1, 0, 0);
    const t = iso(gx, 0, 4.5);
    const tR = iso(gx + 1, 0, 4.5);
    backWall.push(
      <polygon key={gx} points={pts(b, bR, tR, t)}
        fill="#58606a" stroke="#0a0a0a" strokeWidth="0.4" />
    );
  }

  // ── Left wall (gx=0 face, visible left side) ──────────────────────────────
  const leftWall: React.ReactNode[] = [];
  for (let gy = 0; gy < 14; gy++) {
    const b = iso(0, gy, 0);
    const bD = iso(0, gy + 1, 0);
    const t = iso(0, gy, 4.5);
    const tD = iso(0, gy + 1, 4.5);
    leftWall.push(
      <polygon key={gy} points={pts(bD, b, t, tD)}
        fill="#4e565e" stroke="#0a0a0a" strokeWidth="0.4" />
    );
  }

  // ── Rack rows ─────────────────────────────────────────────────────────────
  const racks: React.ReactNode[] = [];
  const RACK_ROWS = [1, 3, 5, 7];
  const frontRows = new Set([1, 5]); // rows whose front face (gy+1 side) faces viewer
  RACK_ROWS.forEach(gy => {
    for (let i = 0; i < RACK_COUNT; i++) {
      const gx = RACK_START + i;
      racks.push(<Rack key={`r${gx},${gy}`} gx={gx} gy={gy} hasFront={frontRows.has(gy)} />);
    }
  });

  // ── CRAC units at ends of cold aisles ────────────────────────────────────
  const cracs: React.ReactNode[] = [
    <Crac key="crac1" gx={-0.2} gy={1.8} />,
    <Crac key="crac2" gx={RACK_START + RACK_COUNT + 0.2} gy={1.8} />,
    <Crac key="crac3" gx={-0.2} gy={5.8} />,
    <Crac key="crac4" gx={RACK_START + RACK_COUNT + 0.2} gy={5.8} />,
  ];

  // ── Plant room: pipes ─────────────────────────────────────────────────────
  const pipeLen = 13; // gx 0..13
  const pipeGxStart = 0;
  const pipes: React.ReactNode[] = [
    <Pipe key="b1" gx={pipeGxStart} gy={9.20} len={pipeLen} top="#60a5fa" side="#1e40af" />,
    <Pipe key="b2" gx={pipeGxStart} gy={9.50} len={pipeLen} top="#3b82f6" side="#1d4ed8" />,
    <Pipe key="r1" gx={pipeGxStart} gy={9.82} len={pipeLen} top="#f87171" side="#991b1b" />,
    <Pipe key="r2" gx={pipeGxStart} gy={10.12} len={pipeLen} top="#ef4444" side="#b91c1c" />,
    <Pipe key="k1" gx={pipeGxStart} gy={10.45} len={pipeLen} top="#555" side="#222" />,
    <Pipe key="k2" gx={pipeGxStart} gy={10.65} len={pipeLen} top="#444" side="#1a1a1a" />,
  ];

  // ── Chillers (right side of plant room) ──────────────────────────────────
  const chillers: React.ReactNode[] = [];
  for (let i = 0; i < 3; i++) {
    chillers.push(<Chiller key={`ch${i}`} gx={13.5 + i * 2.1} gy={9.5} />);
  }

  // ── UPS banks (left side of plant room) ──────────────────────────────────
  const ups: React.ReactNode[] = [];
  for (let i = 0; i < 3; i++) {
    ups.push(<Ups key={`ups${i}`} gx={0.5 + i * 1.9} gy={10.5} />);
  }

  // ── Cooling towers (front perimeter, gy=13..14) ───────────────────────────
  const coolingTowers: React.ReactNode[] = [];
  for (let i = 0; i < 10; i++) {
    coolingTowers.push(<CoolingTower key={`ct${i}`} gx={i * 2} gy={13.0} />);
  }

  // ── Aisle labels ──────────────────────────────────────────────────────────
  const aisleLabelY = -0.3;
  const labels: React.ReactNode[] = [
    <IsoLabel key="cold1" gx={9} gy={2} gz={aisleLabelY} text="▼ Cold Aisle" color="#93c5fd" size={6} />,
    <IsoLabel key="hot1"  gx={9} gy={4} gz={aisleLabelY} text="▲ Hot Aisle"  color="#fca5a5" size={6} />,
    <IsoLabel key="cold2" gx={9} gy={6} gz={aisleLabelY} text="▼ Cold Aisle" color="#93c5fd" size={6} />,
    <IsoLabel key="hot2"  gx={9} gy={8} gz={aisleLabelY} text="▲ Hot Aisle"  color="#fca5a5" size={6} />,
  ];

  // ── Room labels (floating above scene) ────────────────────────────────────
  const serverRoomLabel = iso(9, 4, 5.5);
  const plantRoomLabel  = iso(9, 11, 4.0);

  return (
    <svg
      viewBox="-220 -110 520 390"
      width="100%"
      height="100%"
      style={{ background: '#1a2535' }}
    >
      {/* subtle grid fade at edges */}
      <defs>
        <radialGradient id="sceneFade" cx="50%" cy="50%" r="70%">
          <stop offset="0%" stopColor="#1a2535" stopOpacity="0" />
          <stop offset="100%" stopColor="#1a2535" stopOpacity="0.5" />
        </radialGradient>
      </defs>

      {/* ── Render back-to-front ── */}

      {/* 1. Walls */}
      {backWall}
      {leftWall}

      {/* 2. Floor */}
      {floorTiles}

      {/* 3. Racks (gy=1 → 7, back to front) */}
      {racks}

      {/* 4. CRAC units */}
      {cracs}

      {/* 5. Pipes */}
      {pipes}

      {/* 6. Chillers + UPS */}
      {chillers}
      {ups}

      {/* 7. Cooling towers (front perimeter) */}
      {coolingTowers}

      {/* 8. Labels */}
      {labels}

      {/* Room labels */}
      <text x={serverRoomLabel.x} y={serverRoomLabel.y - 14}
        fill="white" fontSize="9" fontFamily="sans-serif" fontWeight="bold"
        textAnchor="middle" opacity="0.85" style={{ pointerEvents: 'none' }}>
        Server Room
      </text>
      <text x={plantRoomLabel.x} y={plantRoomLabel.y - 14}
        fill="#e2e8f0" fontSize="9" fontFamily="sans-serif" fontWeight="bold"
        textAnchor="middle" opacity="0.85" style={{ pointerEvents: 'none' }}>
        Plant Room
      </text>

      {/* Pipe legend */}
      <g transform="translate(-200, -95)">
        <rect x="0" y="0" width="110" height="52" rx="4" fill="#0f172a" opacity="0.85" />
        <rect x="8" y="10" width="14" height="6" rx="1" fill="#3b82f6" />
        <text x="26" y="16" fill="#93c5fd" fontSize="7" fontFamily="sans-serif">Cold water supply</text>
        <rect x="8" y="22" width="14" height="6" rx="1" fill="#ef4444" />
        <text x="26" y="28" fill="#fca5a5" fontSize="7" fontFamily="sans-serif">Hot water return</text>
        <rect x="8" y="34" width="14" height="6" rx="1" fill="#555" />
        <text x="26" y="40" fill="#94a3b8" fontSize="7" fontFamily="sans-serif">Power / data cables</text>
      </g>

      {/* Edge fade overlay */}
      <rect x="-220" y="-110" width="520" height="390" fill="url(#sceneFade)" style={{ pointerEvents: 'none' }} />
    </svg>
  );
};

export default IsometricDatacenterView;
